import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";

import type { PortfolioChatDto, UserDto } from "@/api/types";
import type { ChatReplyPayload } from "@/components/ChatWindow/ChatWindow";
import { ChatWindow } from "@/components/ChatWindow/ChatWindow";
import { useChatReplies } from "@/components/ChatWindow/useChatReplies";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { SlashCircle01Icon } from "@/components/Icons/SlashCircle01Icon";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { SIGNALR_EVENTS } from "@/helpers/signalr";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useSlug } from "@/hooks/useSlug";
import { ProjectLoader } from "@/providers/ProjectLoader";
import { SessionUserLoader } from "@/providers/SessionUserLoader";
import { useChatReplyHandler } from "@/queries/chats/helpers";
import { portfolioChatMutations } from "@/queries/portfolio-chat/mutations";
import { usePortfolioChatQueries } from "@/queries/portfolio-chat/queryOptions";
import { useUserQueries } from "@/queries/users/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

type PortfolioPrivateChatDetailsModalProps = ModalBaseProps & {
  user?: UserDto;
};

export function PortfolioPrivateChatDetailsModal({
  isOpened,
  onOpenChange,
  onExitComplete,
  user,
}: PortfolioPrivateChatDetailsModalProps): React.ReactNode {
  const portfolioChatQueries = usePortfolioChatQueries();

  // This can be `undefined` as there were no previous chats between the users
  const {
    data: chatDetails,
    isPending: isPendingChatDetails,
    error: errorChatDetails,
  } = useQuery({
    ...portfolioChatQueries.privateChatDetails({ userId: user?.id || "" }),
    enabled: Boolean(user),
  });
  // Hoist optional accesses into locals. The React Compiler (oxc) strips the
  // `?.` when it lifts a member access into a cache-dependency key, so reading
  // `chatDetails?.id` inline compiles to an unguarded `chatDetails.id` that
  // throws once chatDetails is undefined (no prior chat). A local keeps the
  // guard.
  const chatId = chatDetails?.id;
  const projectName = chatDetails?.project?.name;
  const { isLoading: isLoadingReplies, error: errorReplies } = useInfiniteQuery({
    ...portfolioChatQueries.listChatRepliesInfinite({ chatId: chatId || "" }),
    enabled: Boolean(chatDetails),
  });

  const isLoading = !user || isPendingChatDetails || isLoadingReplies;
  const error = errorChatDetails || errorReplies;

  return (
    <Modal.Root
      title={user?.fullName}
      badgeComponent={projectName && <Badge>{projectName}</Badge>}
      size="fullscreen"
      data-testid="portfolio-private-chat-details-modal"
      {...{ isOpened, onOpenChange, onExitComplete }}
    >
      <Modal.Body>
        {isLoading && <FullSizeLoader size="sm" noDelay />}
        {error && <ErrorPage error={error} />}
        {!isLoading && !error && (
          <ProjectLoader projectId={user.projectId}>
            <SessionUserLoader>
              <PortfolioPrivateChatDetailsModalContent userId={user.id} chat={chatDetails} />
            </SessionUserLoader>
          </ProjectLoader>
        )}
      </Modal.Body>
    </Modal.Root>
  );
}

type PortfolioPrivateChatDetailsModalContentProps = {
  userId: string;
  chat: PortfolioChatDto | undefined;
};

function PortfolioPrivateChatDetailsModalContent({
  userId,
  chat,
}: PortfolioPrivateChatDetailsModalContentProps): React.ReactNode {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const postHog = usePostHog();
  const slug = useSlug();

  const portfolioChatQueries = usePortfolioChatQueries();
  const userQueries = useUserQueries();
  const { refetch: refetchChatDetails } = useQuery(portfolioChatQueries.privateChatDetails({ userId }));
  const {
    data: repliesData,
    hasNextPage: hasMoreReplies,
    fetchNextPage: fetchMoreReplies,
    isFetchingNextPage: isLoadingMoreReplies,
  } = useInfiniteQuery({
    ...portfolioChatQueries.listChatRepliesInfinite({ chatId: chat?.id || "" }),
    enabled: Boolean(chat),
  });
  const {
    data: receiverUser,
    isFetching: isFetchingReceiverUser,
    error: errorReceiverUser,
  } = useQuery(
    userQueries.details({
      userId,
    }),
  );
  const addChatMutation = portfolioChatMutations.useAddPrivateChat();
  const addChatReplyMutation = portfolioChatMutations.useAddChatReply();
  const blockChatMutation = portfolioChatMutations.useBlockUser();
  const unblockChatMutation = portfolioChatMutations.useUnblockUser();

  const handleReceiveNewReply = useCallback(() => {
    // If the chat was previously blocked but a new reply is received,
    // refetch chat details data
    if (chat?.blockStatus?.blockedByOtherUser) void refetchChatDetails();
  }, [chat?.blockStatus?.blockedByOtherUser, refetchChatDetails]);

  const { chatReplies } = useChatReplies({
    chatId: chat?.id,
    repliesData,
    signalREvent: SIGNALR_EVENTS.CHAT.NEW_PROJECT_CHAT_REPLY_RECEIVED,
    disabled: !chat,
    onReceiveNewReply: handleReceiveNewReply,
  });
  const { sendReply } = useChatReplyHandler({ addChatReplyMutation });

  const handleAddChatReply = async ({ message, images, videos }: ChatReplyPayload) => {
    let chatId = chat?.id;
    if (!chatId) {
      chatId = await addChatMutation.mutateAsync({ userId }).then((x) => x.id);
    }

    if (chatId) {
      return await sendReply({ message, images, videos, chatId });
    }

    throw new Error("Could not create a chat for this reply");
  };

  const handleBlockUser = () => {
    if (!receiverUser) {
      return;
    }

    postHog.capture("clicked_block_chat");
    blockChatMutation.mutate(receiverUser.id);
  };

  const handleUnblockUser = () => {
    if (!receiverUser) {
      return;
    }

    postHog.capture("clicked_unblock_chat");
    unblockChatMutation.mutate(receiverUser.id);
  };

  const user = chat?.privateChatInfo?.receiver || receiverUser;
  const canBlockUser =
    chat &&
    userId !== sessionUser.id &&
    !receiverUser?.isAdmin &&
    !receiverUser?.deletedAt &&
    !chat.blockStatus?.blockedByRequester;
  const canUnblockUser =
    chat && !receiverUser?.isAdmin && !receiverUser?.deletedAt && chat.blockStatus?.blockedByRequester;
  // If no chat history, the ability to start a new chat depends on both users' chat permissions
  const canStartNewChat =
    sessionUser.chatPermission !== "disabled" && user?.chatPermission === "enabled" && !user?.deletedAt;
  const canSendChatReply = chat ? chat.canReply : canStartNewChat;

  if (isFetchingReceiverUser) {
    return <FullSizeLoader size="sm" noDelay />;
  }

  if (errorReceiverUser) {
    return <ErrorPage error={errorReceiverUser} />;
  }

  return (
    <section className="flex h-full flex-col gap-space-md">
      {receiverUser && (canUnblockUser || canBlockUser) && (
        <Inline gap="sm" justify="end" wrap={false}>
          {canBlockUser && (
            <Button
              data-testid="block-user-private-chat-btn"
              variant="soft"
              icon={<SlashCircle01Icon />}
              isLoading={blockChatMutation.isPending}
              onClick={handleBlockUser}
            >
              {t("page.user-detail.actions.block-private-chat-from-user")}
            </Button>
          )}
          {canUnblockUser && (
            <Button
              data-testid="unblock-user-private-chat-btn"
              variant="soft"
              size="sm"
              icon={<SlashCircle01Icon />}
              isLoading={unblockChatMutation.isPending}
              onClick={handleUnblockUser}
            >
              {t("page.user-detail.actions.unblock-private-chat-from-user")}
            </Button>
          )}
          <Button
            type="link"
            target="_blank"
            href={routes.users.details({
              id: receiverUser.id,
              slug,
            })}
          >
            {t("page.portfolio.chats.private-chat-modal.btn.see-user-details")}
          </Button>
        </Inline>
      )}

      <section className="flex min-h-0 flex-1 flex-col [&>div]:max-h-none! [&>div]:min-h-0 [&>div]:flex-1">
        <ChatWindow
          canSendChatReply={canSendChatReply}
          chatReplies={chatReplies}
          hasMoreReplies={hasMoreReplies}
          statuses={{
            isDisabledByReceiver:
              receiverUser?.chatPermission === "disabled" || receiverUser?.chatPermission === "sendOnly",
            isDisabledBySender: sessionUser.chatPermission === "disabled",
            isBlockedBySender: chat?.blockStatus?.blockedByRequester,
            isBlockedByReceiver: chat?.blockStatus?.blockedByOtherUser,
          }}
          fetchMoreReplies={fetchMoreReplies}
          isLoadingMoreReplies={isLoadingMoreReplies}
          sendReply={handleAddChatReply}
        />
      </section>
    </section>
  );
}
